import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { removeToken } from '../utils/auth';
import ChangePasswordModal from './ChangePasswordModal';
import { useAppContext } from '../contexts/AppContext';
import { apiEndpoints } from '../services/api';

const navItems = [
  { path: '/', label: 'Dashboard', icon: 'dashboard' },
  {
    path: '/vehicles', label: 'Permanent Motor Vehicles', icon: 'vehicles',
    children: [{ path: '/vehicles/temporary', label: 'Temporary Motor Vehicles' }],
  },
  { path: '/documents', label: 'Acts & Documents', icon: 'documents' },
  { path: '/companies', label: 'Company', icon: 'companies' },
  { path: '/alerts', label: 'Alerts', icon: 'alerts' },
  { path: '/suppliers', label: 'Providers', icon: 'suppliers' },
  { path: '/events', label: 'Events Calendar', icon: 'events' },
  { path: '/accidents', label: 'Accidents', icon: 'accidents' },
  { path: '/costs', label: 'Costs', icon: 'costs' },
  { path: '/leasing', label: 'Leasing', icon: 'leasing' },
  { path: '/drivers', label: 'Drivers', icon: 'drivers' },
  { path: '/log', label: 'Log', icon: 'log', adminOnly: true },
];

const SideBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, bumpPageReset } = useAppContext();
  const [showChangePw, setShowChangePw] = useState(false);
  const [openMenu, setOpenMenu] = useState(
    () => navItems.find((item) => item.children?.some((c) => c.path === location.pathname))?.path || null
  );

  const isActive = (path) => location.pathname === path;

  // Keep the parent expanded whenever you're actually on one of its child
  // pages, so the submenu never hides the page you're currently viewing.
  useEffect(() => {
    const parent = navItems.find((item) => item.children?.some((c) => c.path === location.pathname));
    if (parent) setOpenMenu(parent.path);
  }, [location.pathname]);

  // Clicking the link for the page you're already on doesn't change the URL,
  // so React Router won't reset it — force it back to its default view instead.
  const handleNavClick = (path) => {
    if (isActive(path)) bumpPageReset();
    else navigate(path);
  };

  const toggleMenu = (path, e) => {
    e.stopPropagation();
    setOpenMenu((prev) => (prev === path ? null : path));
  };

  const handleLogout = () => {
    apiEndpoints.auth.logout().catch(() => {}); // best-effort audit log entry, don't block logout on it
    removeToken();
    navigate('/login');
  };

  const renderIcon = (type) => {
    switch (type) {
      case 'dashboard':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" rx="1.5"/>
            <rect x="14" y="3" width="7" height="7" rx="1.5"/>
            <rect x="3" y="14" width="7" height="7" rx="1.5"/>
            <rect x="14" y="14" width="7" height="7" rx="1.5"/>
          </svg>
        );
      case 'vehicles':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 17H3v-5l3.5-6H18l3 6v5h-2"/>
            <circle cx="7.5" cy="17" r="2.5"/>
            <circle cx="16.5" cy="17" r="2.5"/>
            <path d="M10 17h4"/>
            <path d="M7.5 11.5h9"/>
          </svg>
        );
      case 'documents':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="8" y1="13" x2="16" y2="13"/>
            <line x1="8" y1="17" x2="13" y2="17"/>
          </svg>
        );
      case 'companies':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="2" width="16" height="20" rx="1.5"/>
            <path d="M9 22V14h6v8"/>
            <path d="M8 6h.01M12 6h.01M16 6h.01"/>
            <path d="M8 10h.01M12 10h.01M16 10h.01"/>
          </svg>
        );
      case 'alerts':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 01-3.46 0"/>
            <circle cx="18" cy="5" r="2.5" fill="#ef4444" stroke="none"/>
          </svg>
        );
      case 'events':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
            <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01"/>
          </svg>
        );
      case 'accidents':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <circle cx="12" cy="17" r="0.75" fill="currentColor" stroke="none"/>
          </svg>
        );
      case 'costs':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 12V8H6a2 2 0 010-4h14v4"/>
            <path d="M4 6v13a2 2 0 002 2h14V12"/>
            <circle cx="17" cy="12" r="1.5"/>
          </svg>
        );
      case 'leasing':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="5" width="20" height="14" rx="2"/>
            <line x1="2" y1="10" x2="22" y2="10"/>
            <path d="M6 15h3"/>
            <path d="M15 15h3"/>
          </svg>
        );
      case 'drivers':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="8" r="4"/>
            <path d="M5 20v-1a7 7 0 0114 0v1"/>
          </svg>
        );
      case 'suppliers':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
        );
      case 'log':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9"/>
            <polyline points="12 7 12 12 15.5 14"/>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <aside className="sidebar-desktop">
      <div className="sidebar-brand">
        <img src="/logo-white-fleet.png" alt="Fleet OS" style={{ height: 68, width: '100%', objectFit: 'contain' }} />
      </div>

      <nav className="sidebar-nav">
        {navItems.filter((item) => !item.adminOnly || isAdmin).map((item) => {
          const hasChildren = !!item.children?.length;
          const expanded = hasChildren && openMenu === item.path;
          return (
            <React.Fragment key={item.path}>
              <button
                className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => handleNavClick(item.path)}
              >
                <span className="sidebar-icon">{renderIcon(item.icon)}</span>
                <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
                {hasChildren && (
                  <span
                    role="button"
                    aria-label={expanded ? 'Collapse' : 'Expand'}
                    onClick={(e) => toggleMenu(item.path, e)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 20, height: 20, flexShrink: 0,
                      transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform var(--t-base)',
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 4.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L11.586 10 7.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </span>
                )}
              </button>
              {hasChildren && expanded && item.children.map((child) => (
                <button
                  key={child.path}
                  className={`sidebar-link sidebar-sublink ${isActive(child.path) ? 'active' : ''}`}
                  onClick={() => handleNavClick(child.path)}
                >
                  <span>{child.label}</span>
                </button>
              ))}
            </React.Fragment>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        {isAdmin && (
        <button
          onClick={() => setShowChangePw(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            width: '100%', padding: '10px 14px', border: 'none',
            borderRadius: '8px', background: 'transparent',
            color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: '13px',
            fontWeight: 600, fontFamily: 'inherit', marginBottom: '2px',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0110 0v4"/>
          </svg>
          Change Password
        </button>
        )}
        <button
          onClick={handleLogout}
          style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            width: '100%', padding: '10px 14px', border: 'none',
            borderRadius: '8px', background: 'transparent',
            color: '#ef4444', cursor: 'pointer', fontSize: '13px',
            fontWeight: 600, fontFamily: 'inherit',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#fff1f0'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          Logout
        </button>
      </div>

      <ChangePasswordModal isOpen={showChangePw} onClose={() => setShowChangePw(false)} />
    </aside>
  );
};

export default SideBar;
